<script setup lang="ts">
import { Button, ZhDecisionCard } from '@zerohuman/ui'
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { api, formatWaited, type ProposedWorkBatch, type ProposedWorkChoice } from '@/api'

/**
 * #849: what one run proposed for a team's work list, as one decision: each item with Approve, Hold and Drop, and
 * Approve all. Needs you lists it as a card; a chat thread draws it inline under the lead's message. Either way each
 * answer goes through the same endpoint, and so through the work list's own rules, and the batch is read again from
 * the answer: an item decided on the board or in the thread is gone from it at the next read, and the card says
 * "All decided" once the last item is (`batch` null, in a thread).
 */
const props = withDefaults(
    defineProps<{
        /** The batch as last read, or null once it has closed: a thread keeps the card, settled. */
        batch: ProposedWorkBatch | null
        variant?: 'card' | 'inline'
        /** The clock the wait is measured on. */
        now?: number
    }>(),
    { variant: 'card', now: () => Date.now() },
)

const emit = defineEmits<{ decided: [batch: ProposedWorkBatch | null] }>()

/** The item being decided (`all` for Approve all), so its buttons wait for the answer. */
const busy = ref<string | null>(null)
const error = ref<string | null>(null)

const count = computed(() => props.batch?.items.length ?? 0)

const decision = computed(() => {
    const batch = props.batch
    const what = count.value === 1 ? '1 proposed item' : `${count.value} proposed items`
    const by = batch?.taskName ?? 'A run'
    return {
        title: batch ? `Approve ${what}` : 'Proposed work',
        why: batch
            ? `${by} proposed ${count.value === 1 ? 'it' : 'them'} for ${batch.teamName ?? 'a team'}'s work list. Nothing starts until you decide.`
            : undefined,
        from: batch?.teamName ?? 'Work list',
        by: batch?.leadName ?? undefined,
        waited: batch ? formatWaited(Math.max(0, props.now - new Date(batch.since).getTime())) : undefined,
    }
})

async function decide(choice: ProposedWorkChoice, ids?: string[]): Promise<void> {
    const batch = props.batch
    if (!batch || busy.value) {
        return
    }
    error.value = null
    busy.value = ids?.[0] ?? 'all'
    try {
        const answer = await api.decideProposedWork(batch.runId, choice, ids)
        emit('decided', answer.batch)
    } catch {
        error.value = 'Could not record the decision (already decided?).'
        emit('decided', batch)
    } finally {
        busy.value = null
    }
}
</script>

<template>
	<ZhDecisionCard
		data-testid="proposed-work-card"
		:data-run-id="batch?.runId"
		:variant="variant"
		:decision="decision"
		kind="action"
		status="Waiting for your decision"
		:settled="batch ? undefined : 'closed'"
		:decided-label="batch ? undefined : 'All decided'"
	>
		<template v-if="batch" #actions>
			<div class="w-full space-y-3 text-sm">
				<ol class="m-0 list-none space-y-2 p-0" aria-label="Proposed items">
					<li
						v-for="(item, index) in batch.items"
						:key="item.id"
						class="flex flex-wrap items-center justify-between gap-2"
						:data-proposed-item="item.id"
					>
						<span class="min-w-0">
							<span class="text-muted-foreground">{{ index + 1 }}.</span>
							<span class="text-foreground">{{ item.title }}</span>
							<span class="text-muted-foreground"> · {{ item.kind }}</span>
						</span>
						<span class="flex gap-1">
							<Button
								type="button"
								size="sm"
								:disabled="busy !== null"
								:aria-label="`Approve ${item.title}`"
								@click="decide('approve', [item.id])"
							>
								Approve
							</Button>
							<Button
								type="button"
								size="sm"
								variant="outline"
								:disabled="busy !== null"
								:aria-label="`Hold ${item.title}`"
								@click="decide('hold', [item.id])"
							>
								Hold
							</Button>
							<Button
								type="button"
								size="sm"
								variant="ghost"
								:disabled="busy !== null"
								:aria-label="`Drop ${item.title}`"
								@click="decide('drop', [item.id])"
							>
								Drop
							</Button>
						</span>
					</li>
				</ol>
				<div class="flex flex-wrap items-center gap-3">
					<Button type="button" size="sm" :disabled="busy !== null" @click="decide('approve')">Approve all</Button>
					<RouterLink class="underline-offset-2 hover:underline" :to="`/runs/${batch.runId}`">Open the run</RouterLink>
				</div>
				<p v-if="error" role="alert" class="text-destructive">{{ error }}</p>
			</div>
		</template>
	</ZhDecisionCard>
</template>
